import {Pressable, Text, View} from "react-native"
import React, {useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import Contact from "models/contact"
import ContactRelationship from "models/contact-relationship"
import ContactRelationshipsTable from "components/contact-relationships/table"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import memo from "set-state-compare/build/memo"
import Nav from "./nav"
import Routes from "shared/routes"
import {useAdminLayout} from "components/admin/layout"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"

/** Admin contact relationships route. */
export default memo(shapeComponent(class RoutesAdminContactsRelationships extends ShapeComponent {
  /** @type {Array<string>} */
  static relationshipsPreload = ["child.contact_type", "child.school_class.class_step", "parent.contact_type", "parent.school_class.class_step"]
  /** @type {Record<string, Array<string>>} */
  static relationshipsSelect = {
    ClassStep: ["id", "name"],
    Contact: ["firstName", "id", "lastName", "name", "pointsLastThirtyDays"],
    ContactRelationship: ["childId", "contactRelationshipTypeId", "createdAt", "id", "parentId", "updatedAt"],
    ContactType: ["id", "identifier"],
    SchoolClass: ["id"]
  }

  /** @returns {void} */
  setup() {
    const {t} = useI18n({namespace: "js.routes.admin.contacts.relationships"})
    const params = useParams()
    const {contact, contactId} = useModel(Contact, {
      match: {params},
      select: {
        Contact: ["contactPointsCount", "id", "name"]
      }
    })
    /** @type {import("@kaspernj/api-maker/build/collection.js").default} */
    const parentsQuery = useMemo(
      () =>
        ContactRelationship
          .ransack({child_id_eq: contactId})
          .preload(this.constructor.relationshipsPreload)
          .select(this.constructor.relationshipsSelect),
      [contactId]
    )
    /** @type {import("@kaspernj/api-maker/build/collection.js").default} */
    const childrenQuery = useMemo(
      () =>
        ContactRelationship
          .ransack({parent_id_eq: contactId})
          .preload(this.constructor.relationshipsPreload)
          .select(this.constructor.relationshipsSelect),
      [contactId]
    )

    this.childrenQuery = childrenQuery
    this.contact = contact
    this.contactId = contactId
    this.parentsQuery = parentsQuery
    this.t = t

    useAdminLayout()?.setState({active: "contacts", headerTitle: t(".relationships")})
  }

  /** @returns {React.ReactNode} */
  render() {
    const {childrenQuery, contact, parentsQuery, t} = this

    return (
      <View dataSet={this.rootViewDataSet ||= {route: "admin/contacts/relationships"}} testID="admin-contact-relationships-route">
        {contact &&
          <Nav
            active="relationships"
            contact={contact}
            style={this.navStyle ||= {marginBottom: 10}}
            testID="admin-contact-relationships-nav"
          />
        }
        <View style={this.tablesContainerViewStyle ||= {width: "100%"}} testID="admin-contact-relationships-content">
          {contact &&
            <>
              <ContactRelationshipsTable
                adminLayout
                collection={parentsQuery}
                controls={this.parentsControls}
                header={Contact.humanAttributeName("parents")}
                identifier="contact-relationships-parents"
                relationDirection="parent"
                styles={this.parentsTableStyles ||= {container: {width: "100%", marginBottom: 24}}}
                testID="admin-contact-relationships-parents-table"
              />
              <View
                style={this.relationshipDiagramContainerStyle ||= {alignItems: "center", marginBottom: 24}}
                testID="admin-contact-relationships-diagram"
              >
                <Text
                  style={this.relationshipsLabelStyle ||= {color: "#4b5563", fontSize: 13, fontWeight: 600}}
                  testID="admin-contact-relationships-parent-label"
                >
                  {Contact.humanAttributeName("parentRelationships")}
                </Text>
                <Text style={this.arrowStyle ||= {color: "#111827", fontSize: 20, lineHeight: 20}} testID="admin-contact-relationships-parent-arrow">
                  ↑
                </Text>
                <View style={this.verticalLineStyle ||= {backgroundColor: "#111827", height: 20, width: 2}} testID="admin-contact-relationships-parent-line" />
                <View
                  style={this.contactCircleStyle ||= {
                    alignItems: "center",
                    borderColor: "#111827",
                    borderRadius: 999,
                    borderWidth: 3,
                    minWidth: 220,
                    paddingHorizontal: 30,
                    paddingVertical: 20
                  }}
                  testID="admin-contact-relationships-contact-circle"
                >
                  <Text
                    style={this.contactCircleLabelStyle ||= {color: "#111827", fontSize: 28, fontWeight: 700, textAlign: "center"}}
                    testID="admin-contact-relationships-contact-name"
                  >
                    {contact.name()}
                  </Text>
                </View>
                <View
                  style={this.verticalLineBottomStyle ||= {backgroundColor: "#111827", height: 20, width: 2}}
                  testID="admin-contact-relationships-child-line"
                />
                <Text style={this.arrowBottomStyle ||= {color: "#111827", fontSize: 20, lineHeight: 20}} testID="admin-contact-relationships-child-arrow">
                  ↓
                </Text>
                <Text
                  style={this.relationshipsBottomLabelStyle ||= {color: "#4b5563", fontSize: 13, fontWeight: 600}}
                  testID="admin-contact-relationships-child-label"
                >
                  {t("activerecord.attributes.contact.children")}
                </Text>
              </View>
              <ContactRelationshipsTable
                adminLayout
                collection={childrenQuery}
                controls={this.childrenControls}
                header={Contact.humanAttributeName("children")}
                identifier="contact-relationships-children"
                relationDirection="child"
                styles={this.childrenTableStyles ||= {container: {width: "100%"}}}
                testID="admin-contact-relationships-children-table"
              />
            </>
          }
        </View>
      </View>
    )
  }

  /** @returns {React.ReactNode} */
  childrenControls = () => <>
    <Pressable
      onPress={this.onAddChildPress}
      testID="admin-contact-relationships-add-child"
    >
      <Icon name="pen" size={16} />
    </Pressable>
  </>

  /** @returns {void} */
  onAddChildPress = () => AppHistory.push(Routes.newAdminContactRelationshipPath({contact_relationship: {parent_id: this.contactId}}))
  /** @returns {void} */
  onAddParentPress = () => AppHistory.push(Routes.newAdminContactRelationshipPath({contact_relationship: {child_id: this.contactId}}))

  /** @returns {React.ReactNode} */
  parentsControls = () => <>
    <Pressable
      onPress={this.onAddParentPress}
      testID="admin-contact-relationships-add-parent"
    >
      <Icon name="pen" size={16} />
    </Pressable>
  </>
}))
